/**
 * Main App Component
 * Root component with header, tab navigation, dashboard views, and footer
 */

import { useState, useCallback } from 'react';
import { useKeystoneSession } from '@/hooks/useKeystoneSession';
import { LandingPage } from './components/LandingPage';
import { Dashboard } from './components/Dashboard';
import { CashPnlDashboard } from './components/CashPnlDashboard';
import { ComplianceDashboard } from './components/ComplianceDashboard';
import { AlertsDashboard } from './components/AlertsDashboard';
import { AIInsightsPanel } from './components/AIInsightsPanel';
import { ScalingUpDashboard } from './components/ScalingUpDashboard';
import { RevenueTargetDashboard } from './components/RevenueTargetDashboard';
import { AIPromptsEditor } from './components/AIPromptsEditor';
import { IntegrationSettings } from './components/IntegrationSettings';
import { TabNav } from './components/TabNav';
import type { TabId } from './components/TabNav';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SessionBadge } from './components/SessionBadge';
import './App.css';

/** Sign-in failures come back as ?auth_error=... on the landing route */
function readAuthError(): string | null {
  const raw = new URLSearchParams(window.location.search).get('auth_error');
  if (!raw) return null;
  // Server-supplied code; render as text, never as markup (C3).
  return raw === 'access_denied'
    ? 'That account does not have access to this dashboard. Ask an administrator to grant it.'
    : 'Sign-in did not complete. Please try again.';
}

export function App() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [alertCount, setAlertCount] = useState(0);
  const { authenticated, loading: sessionLoading, signIn } =
    useKeystoneSession();

  const handleTabChange = useCallback((tab: TabId) => {
    setActiveTab(tab);
  }, []);

  const handleAlertCountChange = useCallback((count: number) => {
    setAlertCount(count);
  }, []);

  // Hold the frame until the session is known. Rendering the dashboard
  // first and swapping it out would flash a screenful of failing panels
  // at a signed-out visitor — the exact thing this landing page fixes.
  if (sessionLoading) {
    return (
      <div className="app-session-gate" role="status" aria-live="polite">
        <span className="loading-spinner" aria-hidden="true" />
        <span className="app-session-gate__label">Checking your session…</span>
      </div>
    );
  }

  if (!authenticated) {
    return <LandingPage onSignIn={signIn} authError={readAuthError()} />;
  }

  return (
    <div className="app">
      {/* Skip to main content link (WCAG 2.2) */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header with Alterspective branding */}
      <header className="app-header" role="banner">
        <div className="container">
          <div className="app-header__content">
            <div className="app-header__logo">
              <img
                src="/alterspective-logo.png"
                alt="Alterspective"
                className="app-header__logo-img"
              />
            </div>
            <nav className="app-header__nav" role="navigation" aria-label="Main navigation">
              <a href="#dashboard" className="app-header__nav-link">
                Dashboard
              </a>
            </nav>
            <SessionBadge />
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <TabNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        alertCount={alertCount}
      />

      {/* Main Content */}
      <main id="main-content" className="app-main" role="main">
        <div className="container">
          <ErrorBoundary>
            <div
              id={`tabpanel-${activeTab}`}
              role="tabpanel"
              aria-labelledby={`tab-${activeTab}`}
            >
              {activeTab === 'overview' && <Dashboard />}
              {activeTab === 'cash-pnl' && <CashPnlDashboard />}
              {activeTab === 'compliance' && <ComplianceDashboard />}
              {activeTab === 'alerts' && (
                <AlertsDashboard onAlertCountChange={handleAlertCountChange} />
              )}
              {activeTab === 'ai-insights' && <AIInsightsPanel />}
              {activeTab === 'scaling-up' && <ScalingUpDashboard />}
              {activeTab === 'revenue-target' && <RevenueTargetDashboard />}
              {activeTab === 'settings' && (
                <>
                  <IntegrationSettings />
                  <AIPromptsEditor />
                </>
              )}
            </div>
          </ErrorBoundary>
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer" role="contentinfo">
        <div className="container">
          <div className="app-footer__content">
            <p className="app-footer__text">
              &copy; {new Date().getFullYear()} Alterspective Pty Ltd. All rights reserved.
            </p>
            <p className="app-footer__attribution">
              Powered by Xero API
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
